export type PickupAssignmentLayoutMode = "three_column" | "map_top";

export type PickupAssignmentLayoutWidths = {
  /** Three-column: drivers | unassigned | map (percents, sum ~100). */
  threeColumn: [number, number, number];
  /** Map on top: outer [map, lists row]; inner [drivers, unassigned]. */
  mapTop: { outer: [number, number]; inner: [number, number] };
};

const defaultWidths: PickupAssignmentLayoutWidths = {
  threeColumn: [33, 33, 34],
  /** Favor taller list row: map ~30%, lists ~70% */
  mapTop: { outer: [30, 70], inner: [50, 50] },
};

export type PickupAssignmentLayoutPreferences = {
  layoutMode: PickupAssignmentLayoutMode;
  widths: PickupAssignmentLayoutWidths;
  /** Map-on-top layout only: slim map header, lists use remaining viewport height. */
  mapTopMapCollapsed?: boolean;
  /**
   * Extra pixels added to the assignment workspace height (map-on-top lists row, or three-column
   * block) so driver / unassigned lists can grow beyond the viewport; the page scrolls.
   */
  mapTopListsExtraPx?: number;
};

export function getDefaultPickupAssignmentLayoutWidths(): PickupAssignmentLayoutWidths {
  return {
    threeColumn: [...defaultWidths.threeColumn] as [number, number, number],
    mapTop: {
      outer: [...defaultWidths.mapTop.outer] as [number, number],
      inner: [...defaultWidths.mapTop.inner] as [number, number],
    },
  };
}

const MAP_TOP_LISTS_EXTRA_MAX = 5000;

export function getDefaultPickupAssignmentLayoutPreferences(): PickupAssignmentLayoutPreferences {
  return {
    layoutMode: "three_column",
    widths: getDefaultPickupAssignmentLayoutWidths(),
    mapTopMapCollapsed: false,
    mapTopListsExtraPx: 0,
  };
}

function clampMapTopListsExtraPx(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(MAP_TOP_LISTS_EXTRA_MAX, Math.round(value)));
}

const storageKey = (terminalId: string | number | null | undefined) =>
  `xgs-pickup-assignment-v2-layout:${terminalId ?? "default"}`;

export function loadPickupAssignmentLayoutPreferences(
  terminalId: string | number | null | undefined
): PickupAssignmentLayoutPreferences {
  try {
    const raw = localStorage.getItem(storageKey(terminalId));
    if (!raw) {
      return getDefaultPickupAssignmentLayoutPreferences();
    }
    const parsed = JSON.parse(raw) as Partial<PickupAssignmentLayoutPreferences>;
    const layoutMode =
      parsed.layoutMode === "map_top" || parsed.layoutMode === "three_column"
        ? parsed.layoutMode
        : "three_column";
    const w = parsed.widths;
    const widths: PickupAssignmentLayoutWidths = {
      threeColumn:
        Array.isArray(w?.threeColumn) && w.threeColumn.length === 3
          ? (w.threeColumn as [number, number, number])
          : [...defaultWidths.threeColumn],
      mapTop:
        w?.mapTop?.outer?.length === 2 && w.mapTop.inner?.length === 2
          ? {
              outer: w.mapTop.outer as [number, number],
              inner: w.mapTop.inner as [number, number],
            }
          : {
              outer: [...defaultWidths.mapTop.outer] as [number, number],
              inner: [...defaultWidths.mapTop.inner] as [number, number],
            },
    };
    const mapTopMapCollapsed =
      typeof parsed.mapTopMapCollapsed === "boolean" ? parsed.mapTopMapCollapsed : false;
    const mapTopListsExtraPx = clampMapTopListsExtraPx(parsed.mapTopListsExtraPx);
    return { layoutMode, widths, mapTopMapCollapsed, mapTopListsExtraPx };
  } catch {
    return getDefaultPickupAssignmentLayoutPreferences();
  }
}

export function savePickupAssignmentLayoutPreferences(
  terminalId: string | number | null | undefined,
  prefs: PickupAssignmentLayoutPreferences
): void {
  try {
    localStorage.setItem(storageKey(terminalId), JSON.stringify(prefs));
  } catch {
    /* ignore quota / private mode */
  }
}
